import { useMainContext } from 'context/MainContext';
import { enumRules } from 'enum/enumRules';
import { ICurrency, INumberFormatCurrency } from 'interfaces/ICurrency';
import { IProductPrice } from 'interfaces/IProduct';
import React, { useCallback } from 'react';
import {
  Col,
  FormControl,
  InputGroup,
  OverlayTrigger,
  Row,
  Table,
  Tooltip
} from 'react-bootstrap';
import { PersonCircle } from 'styled-icons/bootstrap';
import { CorporateFare } from 'styled-icons/material';
import { masks } from 'utils/masks';

import TableTooltips from '../TableTooltips';

type Props = {
  list: IProductPrice[] | null;
  setProductPriceList: React.Dispatch<
    React.SetStateAction<IProductPrice[] | null>
  >;
  selectedCurrency: ICurrency;
};

const TablePriceListClient: React.FC<Props> = ({
  list,
  setProductPriceList,
  selectedCurrency
}) => {
  const { setDialog, profile } = useMainContext();
  const productPriceList = list as IProductPrice[];

  // zera Cost e PriceCompany, isto afetara o style de ativo/inativo
  const handlePriceClientField = useCallback(
    (item: IProductPrice, nullPrice: boolean, field: 'cost' | 'price') => {
      setProductPriceList(
        prevState =>
          prevState?.map(x => {
            if (x.ProductId === item.ProductId) {
              return {
                ...x,
                [field === 'cost' ? 'CostClient' : 'PriceClient']: nullPrice
                  ? null
                  : 0
              };
            }
            return x;
          }) || []
      );
    },
    [setProductPriceList]
  );

  const showAlert = useCallback(
    (item: IProductPrice, field: 'cost' | 'price') => {
      const costOrPriceText = field === 'cost' ? 'custo' : 'preço';
      setDialog({
        show: true,
        textBody: `Ao deixar este campo em branco o produto assumirá o ${costOrPriceText} padrão da companhia de origem. Deseja que este produto adote o ${costOrPriceText} padrão da companhia ?`,
        callBackConfirm: () => handlePriceClientField(item, true, field),
        callBackCancel: () => handlePriceClientField(item, false, field)
      });
    },
    [setDialog, handlePriceClientField]
  );

  const handleChange = useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement>,
      item: IProductPrice,
      field: 'cost' | 'price'
    ) => {
      if (e.target.value.length === 0) {
        showAlert(item, field);
      } else {
        const value: number | null = Number(e.target.value);
        setProductPriceList(
          prevState =>
            prevState?.map(x => {
              if (x.ProductId === item.ProductId) {
                return {
                  ...x,
                  [field === 'cost' ? 'CostClient' : 'PriceClient']: value
                };
              }
              return x;
            }) || []
        );
      }
    },
    [setProductPriceList, showAlert]
  );

  const returnTooltip = (object: IProductPrice) =>
    object.PriceClient !== null || object.CostClient !== null ? (
      <Tooltip>Preço ou Custo personalizados para cliente.</Tooltip>
    ) : (
      <Tooltip>Preço ou Custo padrões da companhia.</Tooltip>
    );

  const convertCurrency = (
    value: number,
    numberFormat: INumberFormatCurrency
  ) => {
    const currencyType = Intl.NumberFormat(numberFormat.Locale, {
      style: 'currency',
      currency: numberFormat.Currency
    });

    return currencyType.format(value as number);
  };

  const renderCost = useCallback(
    (productPrice: IProductPrice) => {
      // Caso 1: Se o usuário pode editar o preço do produto
      if (
        profile?.Rules?.includes(enumRules.ProductPrice.CanWriteProductPrice) ||
        profile?.IsAdm
      ) {
        return (
          <InputGroup>
            <InputGroup.Text>
              {(selectedCurrency as ICurrency).Symbol as string}
            </InputGroup.Text>
            <FormControl
              key={`costClient${productPrice.ProductId}${productPrice.CompanyId}`}
              type="text" // Usamos 'text' para permitir a exibição formatada
              inputMode="decimal" // Aceita entrada numérica
              onBlur={e => {
                if (
                  e.target.value.length === 0 &&
                  productPrice.CostClient !== null
                ) {
                  showAlert(productPrice, 'cost');
                }
              }}
              min="0"
              step=".01"
              value={
                productPrice.CostClient !== null
                  ? convertCurrency(
                      productPrice.CostClient,
                      selectedCurrency?.NumberFormat as INumberFormatCurrency
                    )
                  : ''
              }
              onKeyDown={e => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
                  e.preventDefault();
              }}
              onChange={e => {
                masks.currency(e as React.FormEvent<HTMLInputElement>);
                handleChange(
                  e as React.ChangeEvent<HTMLInputElement>,
                  productPrice,
                  'cost'
                );
              }}
            />
          </InputGroup>
        );
      }

      // Caso 2: Se o preço do produto está disponível
      if (productPrice.CostClient !== null) {
        return convertCurrency(
          productPrice.CostClient,
          selectedCurrency?.NumberFormat as INumberFormatCurrency
        );
      }

      // Caso 3: Se nenhum dos casos acima se aplica
      return <div />;
    },
    [selectedCurrency, handleChange, profile, showAlert]
  );

  const renderPrice = useCallback(
    (productPrice: IProductPrice) => {
      // Caso 1: Se o usuário pode editar o preço do produto
      if (
        profile?.Rules?.includes(enumRules.ProductPrice.CanWriteProductPrice) ||
        profile?.IsAdm
      ) {
        return (
          <InputGroup>
            <InputGroup.Text>
              {selectedCurrency?.Symbol || 'R$'}
            </InputGroup.Text>
            <FormControl
              key={`priceClient${productPrice.ProductId}${productPrice.CompanyId}`}
              type="text" // Usamos 'text' para exibir o valor formatado
              inputMode="decimal" // Permite entrada de números com decimais
              onBlur={e => {
                if (
                  e.target.value.length === 0 &&
                  productPrice.PriceClient !== null
                ) {
                  showAlert(productPrice, 'price');
                }
              }}
              min="0"
              step=".01"
              value={
                productPrice.PriceClient !== null
                  ? convertCurrency(
                      productPrice.PriceClient,
                      selectedCurrency?.NumberFormat as INumberFormatCurrency
                    )
                  : ''
              }
              onKeyDown={e => {
                if (e.key === 'ArrowUp' || e.key === 'ArrowDown')
                  e.preventDefault();
              }}
              onChange={e => {
                masks.currency(e as React.FormEvent<HTMLInputElement>);
                handleChange(
                  e as React.ChangeEvent<HTMLInputElement>,
                  productPrice,
                  'price'
                );
              }}
            />
          </InputGroup>
        );
      }

      // Caso 2: Se o preço do produto está disponível
      if (productPrice.PriceClient !== null) {
        return convertCurrency(
          productPrice.PriceClient,
          selectedCurrency?.NumberFormat as INumberFormatCurrency
        );
      }

      // Caso 3: Se nenhum dos casos acima se aplica
      return <div />;
    },
    [handleChange, profile, selectedCurrency, showAlert]
  );

  return (
    <div>
      {productPriceList && productPriceList.length > 0 && (
        <Row>
          <Col>
            <div className="table-thead-fixed">
              <Table className="table text-center" responsive>
                <thead style={{ zIndex: 1000 }}>
                  <tr>
                    <th>Id</th>
                    <th>Nome</th>
                    <th>Nome Interno</th>
                    <th>Tipo</th>
                    <th>
                      Custo Base{' '}
                      <TableTooltips description="Valor de Custo do produto da Companhia." />
                    </th>
                    <th>
                      Preço Base{' '}
                      <TableTooltips description="Preço de venda padrão aplicado pela companhia para seus clientes." />
                    </th>
                    <th>
                      Custo Personalizado{' '}
                      <TableTooltips description="Preço de custo para este cliente. Pode conter um valor personalizado ou assumir o custo padrão da Companhia se estiver em branco." />
                    </th>
                    <th>
                      Preço Personalizado{' '}
                      <TableTooltips description="Preço de venda para este cliente. Pode conter um valor personalizado ou assumir o preço padrão da Companhia se estiver em branco." />
                    </th>
                    <th> </th>
                  </tr>
                </thead>
                <tbody id="tablebody">
                  {productPriceList.map((productPrice: IProductPrice) => (
                    <tr
                      key={`${productPrice.ProductId}${productPrice.CompanyId}${productPrice.ClientId}`}
                      style={{
                        color:
                          productPrice.PriceClient !== null ||
                          productPrice.CostClient !== null
                            ? 'blue'
                            : ''
                      }}
                    >
                      <td>{productPrice.ProductId}</td>
                      <td>{productPrice.ProductName}</td>
                      <td>{productPrice.ProductInternalName}</td>
                      <td>{productPrice.ProductType}</td>
                      <td>
                        {convertCurrency(
                          productPrice.CostCompany as number,
                          selectedCurrency?.NumberFormat as INumberFormatCurrency
                        )}
                      </td>
                      <td>
                        {convertCurrency(
                          productPrice.PriceCompany as number,
                          selectedCurrency?.NumberFormat as INumberFormatCurrency
                        )}
                      </td>
                      <td>{renderCost(productPrice)}</td>
                      <td>{renderPrice(productPrice)}</td>
                      <td>
                        <OverlayTrigger
                          trigger={['click', 'hover']}
                          overlay={returnTooltip(productPrice)}
                        >
                          {productPrice.PriceClient !== null ||
                          productPrice.CostClient !== null ? (
                            <PersonCircle
                              size={20}
                              cursor="pointer"
                              color="blue"
                            />
                          ) : (
                            <CorporateFare size={20} cursor="pointer" />
                          )}
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default TablePriceListClient;
